/** Display helpers for Cursor agent token usage and estimated cost (list rates, not invoiced). */

export type CursorAgentUsageQuality = "actual" | "partial" | "estimated";

/** Local SDK agents use `agent-…` ids. Cloud agents use `bc-…`. */
export function isLocalCursorAgentId(agentId: string | null | undefined): boolean {
  if (agentId == null) {
    return false;
  }
  return agentId.trim().startsWith("agent-");
}

/** Phrase to type in any Cursor chat on this laptop. Keep in sync with cursor-agent-config.ts. */
export function localAgentResumeKeyphrase(ticketId: string): string {
  return `Continue hub ticket ${ticketId.trim()}`;
}

/** Prefill for the Cursor prompt deeplink. Keep in sync with cursor-agent-config.ts. */
export function localAgentResumePrompt(ticketId: string): string {
  return `${localAgentResumeKeyphrase(ticketId)}. Load the stored investigation on the ticket first. Do not re-search the repo unless I ask.`;
}

/** Email-safe Cursor prompt link. Keep in sync with cursor-agent-config.ts. */
export function localAgentResumeDeeplink(ticketId: string): string {
  const url = new URL("https://cursor.com/link/prompt");
  url.searchParams.set("text", localAgentResumePrompt(ticketId));
  return url.toString();
}

export function formatCursorAgentCostUsd(
  raw: string | null,
  inputTokens: string | null,
  outputTokens: string | null,
  usageQuality: CursorAgentUsageQuality | string | null = null,
): string {
  if (raw == null || raw.trim() === "") {
    return "—";
  }
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed)) {
    return "—";
  }
  if (parsed === 0 && inputTokens == null && outputTokens == null) {
    return "—";
  }
  const amount = parsed < 0.01 ? `$${parsed.toFixed(4)}` : `$${parsed.toFixed(2)}`;
  if (usageQuality === "estimated") {
    return `~${amount} (est.)`;
  }
  if (usageQuality === "partial") {
    return `${amount} (output only)`;
  }
  return amount;
}

export function formatCursorAgentTokenCount(
  value: string | null,
  estimated: boolean,
): string {
  if (value == null || value.trim() === "") {
    return estimated ? "~0 (est.)" : "0";
  }
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) {
    return estimated ? "~0 (est.)" : "0";
  }
  const formatted = parsed.toLocaleString();
  return estimated ? `~${formatted} (est.)` : formatted;
}

export function formatCursorAgentTokenSummary(params: {
  inputTokens: string | null;
  outputTokens: string | null;
  cacheReadTokens?: string | null;
  usageQuality?: CursorAgentUsageQuality | string | null;
}): string | null {
  const hasInput = params.inputTokens != null && params.inputTokens.trim() !== "";
  const hasOutput = params.outputTokens != null && params.outputTokens.trim() !== "";
  if (!hasInput && !hasOutput) {
    return null;
  }
  const inputEstimated = params.usageQuality === "estimated";
  const outputEstimated = false;
  const parts = [
    `in ${formatCursorAgentTokenCount(params.inputTokens, inputEstimated)}`,
    `out ${formatCursorAgentTokenCount(params.outputTokens, outputEstimated)}`,
  ];
  if (params.cacheReadTokens != null && params.cacheReadTokens.trim() !== "") {
    parts.push(`cache read ${formatCursorAgentTokenCount(params.cacheReadTokens, false)}`);
  }
  const summary = `Tokens ${parts.join(" · ")}`;
  if (params.usageQuality === "actual") {
    return `${summary} (streamed)`;
  }
  if (params.usageQuality === "partial") {
    return `${summary} (output streamed; input not reported)`;
  }
  if (params.usageQuality === "estimated") {
    return `${summary} (input estimated from output volume)`;
  }
  return summary;
}

export function formatCursorAgentUsageFootnote(
  usageQuality: CursorAgentUsageQuality | string | null,
): string | null {
  if (usageQuality === "actual") {
    return "Token counts came from the agent stream (turn-ended usage). Cost uses Cursor list rates (Composer, Gemini, Grok), not invoiced amounts.";
  }
  if (usageQuality === "partial") {
    return "Only output tokens were reported on the stream. Cost is a floor — real billing is usually higher once input/context is included.";
  }
  if (usageQuality === "estimated") {
    return "Input tokens were not reported; the hub estimated them from output volume for Gemini runs. Treat cost as approximate.";
  }
  return null;
}
